import { useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "../lib/api";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "polling", data: null, error: "" });

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", error: "Missing session id" }); return; }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", data });
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setState({ status: "failed", data });
          return;
        }
        if (attempts >= 20) {
          setState({ status: "timeout", data });
          return;
        }
        timer = setTimeout(poll, 1500);
      } catch (e) {
        setState({ status: "error", error: apiError(e) });
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const map = {
    polling: { icon: Icons.Loader2, tone: "text-elv-cyan", title: "Confirming your payment…",
      body: "Hang tight — we're checking with Stripe.", spin: true },
    paid: { icon: Icons.CheckCircle2, tone: "text-elv-green", title: "Payment successful",
      body: "Thanks — we've recorded your payment and updated your account." },
    failed: { icon: Icons.XCircle, tone: "text-red-400", title: "Payment could not be completed",
      body: "Your card was not charged. Please try a different payment method." },
    timeout: { icon: Icons.Clock, tone: "text-amber-300", title: "Still processing…",
      body: "Your payment is taking longer than usual. It will appear once Stripe confirms it." },
    error: { icon: Icons.AlertTriangle, tone: "text-red-400", title: "Something went wrong",
      body: state.error || "Please contact us if you were charged and this persists." },
  };
  const view = map[state.status];
  const I = view.icon;

  return (
    <div data-testid="payment-success-page" className="flex min-h-screen items-center justify-center bg-elv-base p-6">
      <div className="glass w-full max-w-lg rounded-xl p-8 text-center">
        <I className={`mx-auto h-10 w-10 ${view.tone} ${view.spin ? "animate-spin" : ""}`} />
        <h1 data-testid="payment-title" className="mt-5 text-2xl font-semibold text-elv-text">{view.title}</h1>
        <p className="mt-3 text-sm text-elv-dim">{view.body}</p>
        {state.data?.label ? (
          <p className="mt-4 rounded-md border border-elv-line bg-white/[0.03] px-4 py-2 text-xs text-elv-text">
            {state.data.label} · ${state.data.amount?.toFixed(2)}
          </p>
        ) : null}
        <div className="mt-7 flex flex-wrap justify-center gap-2">
          <button type="button" onClick={() => navigate("/app/payments")} data-testid="back-to-payments"
                  className="rounded-md bg-elv-green px-4 py-2 text-xs font-semibold text-[#04120A]">
            Back to Payments
          </button>
          <Link to="/app/dashboard" className="rounded-md border border-elv-line px-4 py-2 text-xs text-elv-dim hover:text-elv-text">
            Go to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div data-testid="payment-cancel-page" className="flex min-h-screen items-center justify-center bg-elv-base p-6">
      <div className="glass w-full max-w-lg rounded-xl p-8 text-center">
        <Icons.XCircle className="mx-auto h-10 w-10 text-amber-300" />
        <h1 className="mt-5 text-2xl font-semibold text-elv-text">Checkout cancelled</h1>
        <p className="mt-3 text-sm text-elv-dim">
          No card was charged. You can try again anytime from the Payments page.
        </p>
        <div className="mt-7">
          <Link to="/app/payments" data-testid="back-to-payments"
                className="rounded-md bg-elv-green px-4 py-2 text-xs font-semibold text-[#04120A]">
            Back to Payments
          </Link>
        </div>
      </div>
    </div>
  );
}
